import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {KYC_DECLINED_ANIMATION} from './constants';
import {useTrackers} from './trackers';

// region [---- TYPES ----]
type ViewModelContextType = {
  showTitle: boolean;
  showDescription: boolean;
};
// endregion

// region [---- CONTEXT ----]
const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);
// endregion

// region [---- PROVIDER ----]
export function ViewModelProvider({children}: {children: ReactNode}) {
  // region [---- HOOKS ----]
  const {trackViewKycDeclined} = useTrackers();
  const [showTitle, setShowTitle] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  // endregion

  // region [---- EFFECTS ----]
  useEffect(() => {
    trackViewKycDeclined();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const titleTimer = window.setTimeout(() => {
      setShowTitle(true);
    }, KYC_DECLINED_ANIMATION.TITLE_REVEAL_DELAY_MS);

    const descriptionTimer = window.setTimeout(() => {
      setShowDescription(true);
    }, KYC_DECLINED_ANIMATION.DESCRIPTION_REVEAL_DELAY_MS);

    return () => {
      window.clearTimeout(titleTimer);
      window.clearTimeout(descriptionTimer);
    };
  }, []);
  // endregion

  // region [---- MEMOS ----]
  const value = useMemo(
    () => ({
      showTitle,
      showDescription,
    }),
    [showDescription, showTitle],
  );
  // endregion

  return (
    <ViewModelContext.Provider value={value}>
      {children}
    </ViewModelContext.Provider>
  );
}
// endregion

// region [---- CONTEXT HOOK ----]
export function useViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (context === undefined) {
    throw new Error('KycDeclined ViewModelContext is undefined');
  }
  return context;
}
// endregion
